import { useQuery } from '@tanstack/react-query'
import { Link2, ExternalLink, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { seoAnalytics } from '@/lib/api'

interface TopCrawledUrlsTableProps {
  days: number
  section?: string
}

const URL_TYPE_COLORS: Record<string, string> = {
  product: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  category: 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400',
  home: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  cms: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  other: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
}

const URL_TYPE_LABELS: Record<string, string> = {
  product: 'Producto',
  category: 'Categoria',
  home: 'Home',
  cms: 'CMS',
  other: 'Otro',
}

export default function TopCrawledUrlsTable({ days, section }: TopCrawledUrlsTableProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['seo-top-urls', days, section],
    queryFn: () => seoAnalytics.getTopCrawledUrls(days, 50, section),
  })

  const urls = data?.data || []

  // Calculate max hits for progress bar
  const maxHits = urls.length > 0 ? Math.max(...urls.map((u: any) => u.total_hits)) : 1

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-success-100 dark:bg-success-900/30">
            <TrendingUp className="w-5 h-5 text-success-600 dark:text-success-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">URLs Mas Rastreadas</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Paginas con mayor actividad de bots
            </p>
          </div>
        </div>
        {urls.length > 0 && (
          <span className="badge badge-success">{urls.length} URLs</span>
        )}
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="skeleton h-4 w-48" />
                    <div className="skeleton h-4 w-16" />
                  </div>
                  <div className="skeleton h-2 w-full rounded-full" />
                </div>
              ))}
            </div>
          </div>
        ) : urls.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Link2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No hay datos de URLs rastreadas</p>
            <p className="text-sm mt-1">Ejecuta una sincronizacion para obtener datos</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-96 overflow-y-auto">
            {urls.map((url: any, idx: number) => (
              <div
                key={idx}
                className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0',
                        URL_TYPE_COLORS[url.url_type] || URL_TYPE_COLORS.other
                      )}
                    >
                      {URL_TYPE_LABELS[url.url_type] || url.url_type || 'Otro'}
                    </span>
                    <span
                      className="text-sm text-slate-700 dark:text-slate-300 truncate"
                      title={url.url_path}
                    >
                      {url.url_path}
                    </span>
                    <a
                      href={`https://www.jvs-informatica.com${url.url_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors shrink-0"
                      title="Abrir en nueva pestana"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                    </a>
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 ml-4">
                    {url.total_hits?.toLocaleString()}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-success-500 to-success-400 rounded-full transition-all duration-300"
                    style={{ width: `${(url.total_hits / maxHits) * 100}%` }}
                  />
                </div>
                {/* Bot info */}
                {url.bot_name && (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                    <span>Principal: {url.bot_name}</span>
                    {url.unique_bots > 1 && (
                      <span className="text-slate-400">+{url.unique_bots - 1} bots mas</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
